/**
 * TemplateListPanel
 *
 * Phase 4 — Enhanced template selection panel inside the project creation flow.
 *
 * Enhancements over original:
 *  - Category filter pills (All, Consulting, Fixed-Fee Delivery, … , ⭐ Favourites)
 *  - Category badge on each card
 *  - Default duration display ("30 day default")
 *  - Priority badge
 *  - "Using template" banner wired via onTemplateSelected callback
 */

import { useState } from 'react';
import {
  Search,
  LayoutTemplate,
  Star,
  Building2,
  ChevronRight,
  Clock,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import type { ProjectTemplate, TemplateCategory } from '@/types/template.types';
import { TEMPLATE_CATEGORIES } from '@/types/template.types';

// ── Types ──────────────────────────────────────────────────────────────────────

interface TemplateListPanelProps {
  /** The full list of available templates (passed in from context). */
  templates: ProjectTemplate[];
  /** Called when the user confirms a template selection. */
  onSelect: (templateId: string) => void;
  /** Called when the user clicks "← Back to options". */
  onBack: () => void;
  /** Called when the user clicks "Cancel" to close the panel entirely. */
  onCancel: () => void;
}

// ── Colour helpers (mirrors project-templates.tsx) ────────────────────────────

function getCategoryColour(category?: string): string {
  const map: Record<string, string> = {
    'Consulting':         'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    'Fixed-Fee Delivery': 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    'Infrastructure':     'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    'Marketing':          'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
    'Internal':           'bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-300',
    'Other':              'bg-neutral-100 text-neutral-700 dark:bg-neutral-800/60 dark:text-neutral-300',
  };
  return category ? (map[category] ?? map.Other) : map.Other;
}

function getPriorityColour(priority?: string): string {
  const map: Record<string, string> = {
    'Critical': 'bg-error-100 text-error-700 dark:bg-error-900/40 dark:text-error-300',
    'High':     'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    'Medium':   'bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-300',
    'Low':      'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  };
  return priority ? (map[priority] ?? '') : '';
}

// ── Filter pill labels ─────────────────────────────────────────────────────────

type FilterPill = 'All' | TemplateCategory | 'Favourites';

// ── Component ──────────────────────────────────────────────────────────────────

export function TemplateListPanel({
  templates,
  onSelect,
  onBack,
  onCancel,
}: TemplateListPanelProps) {
  const [search, setSearch]       = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterPill>('All');

  // Determine which category pills to show (only categories with ≥1 template)
  const usedCategories = TEMPLATE_CATEGORIES.filter(cat =>
    templates.some(t => t.category === cat),
  );
  const hasFavourites = templates.some(t => t.isFavorite);

  // ── Filtering ────────────────────────────────────────────────────────────────

  const filtered = templates.filter(t => {
    // Category / favourites pill filter
    if (activeFilter === 'Favourites' && !t.isFavorite) return false;
    if (activeFilter !== 'All' && activeFilter !== 'Favourites' && t.category !== activeFilter) return false;

    // Text search
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        t.templateName.toLowerCase().includes(q) ||
        (t.templateDescription ?? '').toLowerCase().includes(q) ||
        (t.type ?? '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Favourites first, then alphabetical
  const sorted = [...filtered].sort((a, b) => {
    if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
    return a.templateName.localeCompare(b.templateName);
  });

  return (
    <div className="flex flex-col gap-3 animate-in slide-in-from-right-2 duration-300">

      {/* ── Search ── */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          placeholder="Search templates…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          autoFocus
          aria-label="Search templates"
        />
      </div>

      {/* ── Category filter pills ── */}
      {(usedCategories.length > 0 || hasFavourites) && (
        <div className="flex flex-wrap gap-1.5">
          {/* All pill */}
          <FilterPillButton
            label="All"
            count={templates.length}
            active={activeFilter === 'All'}
            onClick={() => setActiveFilter('All')}
          />

          {/* Per-category pills */}
          {usedCategories.map(cat => (
            <FilterPillButton
              key={cat}
              label={cat}
              count={templates.filter(t => t.category === cat).length}
              active={activeFilter === cat}
              onClick={() => setActiveFilter(cat)}
            />
          ))}

          {/* Favourites pill — only shown when ≥1 template is starred */}
          {hasFavourites && (
            <FilterPillButton
              label="Favourites"
              count={templates.filter(t => t.isFavorite).length}
              active={activeFilter === 'Favourites'}
              onClick={() => setActiveFilter('Favourites')}
              isFavourites
            />
          )}
        </div>
      )}

      {/* ── Result count ── */}
      {(search.trim() || activeFilter !== 'All') && (
        <p className="text-xs text-muted-foreground -mt-1">
          {sorted.length === 0
            ? 'No templates match your filter'
            : `${sorted.length} template${sorted.length !== 1 ? 's' : ''} found`}
        </p>
      )}

      {/* ── Template cards ── */}
      <ScrollArea className="max-h-[380px]">
        <ScrollAreaViewport>
          <div className="space-y-2 pr-1">
            {sorted.length > 0 ? (
              sorted.map(template => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onSelect={onSelect}
                />
              ))
            ) : (
              <EmptyState search={search} activeFilter={activeFilter} />
            )}
          </div>
        </ScrollAreaViewport>
        <ScrollAreaScrollbar orientation="vertical" />
      </ScrollArea>

      {/* ── Footer ── */}
      <div className="flex justify-between items-center pt-2 border-t mt-auto">
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Back to options
        </Button>
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ── Filter Pill Button ─────────────────────────────────────────────────────────

interface FilterPillButtonProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  isFavourites?: boolean;
}

function FilterPillButton({ label, count, active, onClick, isFavourites }: FilterPillButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
        'border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground',
      )}
    >
      {isFavourites && <Star className="h-3 w-3" />}
      {label}
      <span className={cn(
        'inline-flex items-center justify-center rounded-full px-1 min-w-[16px] h-4 text-[10px] font-semibold',
        active ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground',
      )}>
        {count}
      </span>
    </button>
  );
}

// ── Template Card ──────────────────────────────────────────────────────────────

interface TemplateCardProps {
  template: ProjectTemplate;
  onSelect: (id: string) => void;
}

function TemplateCard({ template, onSelect }: TemplateCardProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(template.id)}
      className={cn(
        'group w-full text-left rounded-lg border bg-card transition-shadow duration-150',
        'hover:shadow-md',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
      )}
      aria-label={`Select template: ${template.templateName}`}
    >
      {/* Card Header */}
      <div className="p-4 pb-2 pr-10 relative">

        {/* Favourite star — top right (display only, mirroring gallery card) */}
        {template.isFavorite && (
          <Star className="absolute top-3 right-3 h-4 w-4 fill-warning-400 text-warning-400" aria-label="Favourite" />
        )}

        {/* Category badge */}
        {template.category && (
          <span className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium mb-1.5 ${getCategoryColour(template.category)}`}>
            {template.category}
          </span>
        )}

        {/* Name */}
        <h3 className="font-semibold text-sm leading-snug text-foreground">
          {template.templateName}
        </h3>

        {/* Description */}
        {template.templateDescription && (
          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5 leading-relaxed">
            {template.templateDescription}
          </p>
        )}
      </div>

      {/* Card Content */}
      <div className="px-4 pb-3 flex flex-col gap-2">

        {/* Badges row — priority + type */}
        {(template.priority || template.type) && (
          <div className="flex flex-wrap gap-1.5">
            {template.priority && (
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getPriorityColour(template.priority)}`}>
                {template.priority}
              </span>
            )}
            {template.type && (
              <Badge variant="outline" className="text-xs h-5">
                {template.type}
              </Badge>
            )}
          </div>
        )}

        {/* Stats row — mirrors gallery card exactly */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">

          {/* Default duration */}
          {template.defaultDurationDays && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {template.defaultDurationDays}d default
            </span>
          )}

          {/* Cost centres */}
          {template.costCentres && template.costCentres.length > 0 && (
            <span className="flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              {template.costCentres.length === 1
                ? '1 cost centre'
                : `${template.costCentres.length} cost centres`}
            </span>
          )}

          {/* Resource budget summary (8.16) */}
          {template.resourceBudget && template.resourceBudget.length > 0 && (() => {
            const totalHours = template.resourceBudget.reduce((s, e) => s + e.totalHours, 0);
            const totalCost  = template.resourceBudget.reduce((s, e) => s + e.totalHours * e.hourlyRate, 0);
            return (
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {template.resourceBudget.length} role{template.resourceBudget.length !== 1 ? 's' : ''} · {totalHours} hrs · £{totalCost.toLocaleString()}
              </span>
            );
          })()}
        </div>

        {/* Select prompt */}
        <div className="flex items-center justify-end text-xs text-primary font-medium pt-0.5 group-hover:underline">
          Select
          <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
        </div>

      </div>
    </button>
  );
}

// ── Empty State ────────────────────────────────────────────────────────────────

function EmptyState({ search, activeFilter }: { search: string; activeFilter: FilterPill }) {
  const hasFilter = activeFilter !== 'All';
  const hasSearch = search.trim().length > 0;

  return (
    <div className="flex flex-col items-center justify-center py-14 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-3">
        <LayoutTemplate className="h-5 w-5 text-muted-foreground opacity-60" />
      </div>
      {hasSearch ? (
        <>
          <p className="text-sm font-medium text-foreground mb-1">No templates found</p>
          <p className="text-xs text-muted-foreground">
            No templates match <span className="font-medium">"{search}"</span>.{' '}
            {hasFilter && 'Try clearing the category filter or '}Try a different search term.
          </p>
        </>
      ) : hasFilter ? (
        <>
          <p className="text-sm font-medium text-foreground mb-1">No templates in this category</p>
          <p className="text-xs text-muted-foreground">
            There are no{' '}
            {activeFilter === 'Favourites' ? 'favourited' : <span className="font-medium">{activeFilter}</span>}{' '}
            templates yet.
          </p>
        </>
      ) : (
        <>
          <p className="text-sm font-medium text-foreground mb-1">No templates yet</p>
          <p className="text-xs text-muted-foreground">
            Templates you create will appear here.
          </p>
        </>
      )}
    </div>
  );
}
